import React, { useMemo, useState } from 'react';
import Tooltip2 from 'components/Common/Tooltip2';
import Icon from 'components/Utils/NewIcon';
import CalendarUtils from 'services/CalendarUtils';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import ReferralTrackingUtils from 'services/ReferralTrackingUtils';
import type { PatientDocument } from 'generated/api';
import './style.scss';

/**
 * "Referred to" — the referral half of the Consultants box in the staff CPP.
 *
 * Rows are derived read-only from the patient's referral documents (see
 * ReferralTrackingUtils), so they carry a date and a live status, which stored
 * consultant entries structurally cannot. The row itself is not editable: the
 * source of truth is the document, so the eye icon opens that document rather
 * than an inline editor.
 */
const CPPReferralsList = ({ documents }: { documents?: PatientDocument[] }) => {
  const [selectedDocument, setSelectedDocument] = useState<PatientDocument | undefined>();

  const rows = useMemo(() => ReferralTrackingUtils.getReferralRows(documents), [documents]);

  if (rows.length === 0) return null;

  return (
    <div className="cpp-referrals-list">
      <span className="small-title list-element">Referred to</span>
      <div className="list">
        {rows.map((row) => {
          const dateText = row.date ? CalendarUtils.format(row.date) : undefined;
          const label = row.specialty ? `${row.name} - ${row.specialty}` : row.name;

          const tooltip = (
            <div>
              <span>{label}</span>
              <br />
              <span>
                {row.statusLabel}
                {dateText ? ` — referred ${dateText}` : ''}
              </span>
            </div>
          );

          // Mirrors the markup of a native CPP row: the icon container comes
          // first and is revealed on hover. Rendering it (rather than leaving
          // the row bare) is what keeps the hover behaviour identical to the
          // stored consultants above — the list reserves that space either way.
          // The handler sits on the container rather than on the Icon (where a
          // native row puts it) so the whole icon box is a hit target, not just
          // the glyph. `.list-element-icons .icon` already sets cursor:pointer.
          const icons = (
            <div
              className="list-element-icons"
              onClick={() => {
                const document = documents?.find((it) => it.id === row.id);
                if (document) setSelectedDocument(document);
              }}
            >
              <Icon src="eye" alt="Open referral" className="preview-icon" />
            </div>
          );

          return (
            <div key={row.id}>
              <Tooltip2 tooltip={tooltip}>
                {(register: any) => (
                  <span
                    className={`tooltip-wrapper list-element truncate referral-row ${row.colorClass}`}
                    {...register}
                  >
                    {icons}
                    {label}
                    <span className="referral-status">({row.statusLabel})</span>
                  </span>
                )}
              </Tooltip2>
            </div>
          );
        })}
      </div>
      <DocumentDetailsModal
        document={selectedDocument}
        isOpen={!!selectedDocument}
        onClose={() => setSelectedDocument(undefined)}
      />
    </div>
  );
};

export default CPPReferralsList;
